import { z } from 'zod';
import { CustomFieldSchema } from './custom-field.dto';
import { inferApiResultSchema, inferPaginatedApiResponse } from './infer-api';
import { type GetAllParamsDto, SortSchema } from './types.dto';

export const ideaViewNameLimit = 300;

export const DEFAULT_MATRIX_VIEW_ROTATION = 'TOP_LEFT';

/**
 * Default radius percentage for matrix evaluation zones (1-100)
 */
export const DEFAULT_MATRIX_VIEW_EVALUATION_ZONE_RADIUS = 50;

/**
 * Default labels for matrix evaluation zones
 */
export const DEFAULT_MATRIX_VIEW_EVALUATION_ZONE_LABELS: string[] = [
  'Must Do',
  'Nice to Have',
  'Consider',
];

/**
 * Label for outside zones
 */
export const DEFAULT_MATRIX_VIEW_OUTSIDE_ZONES_LABEL = 'Optional';

/**
 * Complete default evaluation zones configuration object
 */
export const DEFAULT_MATRIX_VIEW_EVALUATION_ZONES: MatrixViewEvaluationZonesDto = {
  zoneRadius: DEFAULT_MATRIX_VIEW_EVALUATION_ZONE_RADIUS,
  zoneLabels: DEFAULT_MATRIX_VIEW_EVALUATION_ZONE_LABELS,
  outsideZoneLabel: DEFAULT_MATRIX_VIEW_OUTSIDE_ZONES_LABEL,
};

/**
 * Default debounce time for matrix view (in milliseconds)
 */
export const MATRIX_VIEW_SAVE_DEBOUNCE_TIME = 1000;

export const BooleanFilterSchema = z.object({
  operand: z.enum(['eq']),
  value: z.boolean().nullish(),
});

export const NumberFilterSchema = z.object({
  operand: z.enum(['eq', 'neq', 'gt', 'lt', 'gte', 'lte', 'between']),
  value: z.number().nullish(),
  value2: z.number().nullish(),
});

export const DateFilterSchema = z.object({
  operand: z.enum(['between', 'before', 'after', 'on']),
  value: z.string().nullish(),
  value2: z.string().nullish(),
});
export const DateRangeFilterSchema = z.object({
  operand: z.enum(['in', 'nin']),
  value: z.string().nullish(),
});

export const ArrayFilterSchema = z.object({
  operand: z.enum(['in', 'nin']),
  value: z.array(z.number()).or(z.array(z.string())).optional().default([]),
});

export const StringFilterSchema = z.object({
  operand: z.enum(['contains', 'startswith', 'endswith', 'eq', 'neq']),
  value: z.string().nullish(),
});

export const FieldFilterSchema = z.object({
  fieldId: z.number(),
  filter: NumberFilterSchema.nullish()
    .or(ArrayFilterSchema.nullish())
    .or(StringFilterSchema.nullish())
    .or(DateFilterSchema.nullish())
    .or(DateRangeFilterSchema.nullish())
    .or(BooleanFilterSchema.nullish()),
});

export const FilterSchema = z.object({
  fields: z.array(FieldFilterSchema).nullish(),
  internalStatus: ArrayFilterSchema.nullish(),
  productAreas: ArrayFilterSchema.nullish(),
  votes: NumberFilterSchema.nullish(),
  customers: ArrayFilterSchema.nullish(),
  creator: ArrayFilterSchema.nullish(),
  isPublished: BooleanFilterSchema.nullish(),
});

export const GridColumnSchema = z.object({
  type: z.enum([
    'title',
    'votes',
    'status',
    'productArea',
    'createdBy',
    'createdAt',
    'updatedAt',
    'customField',
    'priorityFramework',
    'published',
  ]),
  customFieldId: z.number().nullish(),
  priorityFrameworkId: z.number().nullish(),
  width: z.number().optional(),
});

export type GridColumnDto = z.infer<typeof GridColumnSchema>;

export const GridConfigSchema = z.object({
  columns: z.array(GridColumnSchema),
  sort: SortSchema.optional(),
});

export type GridConfigDto = z.infer<typeof GridConfigSchema>;

export const MatrixFieldsSchema = z.object({
  xFieldId: z.number().nullish(),
  yFieldId: z.number().nullish(),
  sizeFieldId: z.number().nullish(),
  colorFieldId: z.number().nullish(),
});

export type MatrixFieldsDto = z.infer<typeof MatrixFieldsSchema>;

// Matrix rotation enum - represents which corner evaluation zones emanate from
export const MatrixViewRotationSchema = z.enum([
  'TOP_LEFT',
  'TOP_RIGHT',
  'BOTTOM_RIGHT',
  'BOTTOM_LEFT',
]);
export type MatrixViewRotationT = z.infer<typeof MatrixViewRotationSchema>;

// Matrix evaluation zones configuration
export const MatrixViewEvaluationZonesSchema = z.object({
  zoneRadius: z.number().min(1).max(100).default(50),
  zoneLabels: z
    .array(z.string().min(1).max(20))
    .length(3)
    .default(['Must Do', 'Nice to Have', 'Consider']),
  outsideZoneLabel: z.string().min(0).max(20).default('Optional'),
});
export type MatrixViewEvaluationZonesDto = z.infer<typeof MatrixViewEvaluationZonesSchema>;

export const MatrixConfigSchema = z.object({
  isMatrixEnabled: z.boolean(),
  matrixFields: MatrixFieldsSchema,
  matrixRotation: MatrixViewRotationSchema.optional(),
  matrixEvaluationZones: MatrixViewEvaluationZonesSchema.optional(),
});

export type MatrixConfigDto = z.infer<typeof MatrixConfigSchema>;

export type GetAllIdeaViewsDto = GetAllParamsDto & { includeIdeaCount?: boolean };

export const IdeaViewSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  name: z.string().trim().min(1).max(ideaViewNameLimit),
  filter: FilterSchema,
  gridConfig: GridConfigSchema,
  matrixConfig: MatrixConfigSchema,
  ownerId: z.string().length(24),
  updatedAt: z.date(),
  createdAt: z.date(),
  deletedAt: z.coerce.date().nullish(),
  archivedAt: z.coerce.date().nullish(),
  ideaCount: z.number().nullish(),
  rankSort: SortSchema.optional(),
});

export const IdeaViewListEntrySchema = IdeaViewSchema.omit({ gridConfig: true, filter: true });

export type IdeaViewDto = z.infer<typeof IdeaViewSchema>;

export const IdeaViewSortSchema = z.object({
  column: IdeaViewSchema.omit({
    accountId: true,
    filter: true,
  })
    .keyof()
    .default('createdAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type IdeaViewSortDto = z.infer<typeof IdeaViewSortSchema>;

export type FilterSchemaDto = z.infer<typeof FilterSchema>;
export type FieldFilterSchemaDto = z.infer<typeof FieldFilterSchema>;
export type NumberFilterSchemaDto = z.infer<typeof NumberFilterSchema>;
export type BooleanFilterSchemaDto = z.infer<typeof BooleanFilterSchema>;
export type DateFilterSchemaDto = z.infer<typeof DateFilterSchema>;
export type DateRangeFilterSchemaDto = z.infer<typeof DateRangeFilterSchema>;
export type ArrayFilterSchemaDto = z.infer<typeof ArrayFilterSchema>;
export type StringFilterSchemaDto = z.infer<typeof StringFilterSchema>;

// GET /api/v1/idea-views/:id
export const IdeaViewWithCustomFieldsSchema = IdeaViewSchema.extend({
  customFields: z.array(CustomFieldSchema).nullish(),
  customFieldsUpdated: z.boolean().nullish(),
});
export type IdeaViewWithCustomFieldsDto = z.infer<typeof IdeaViewWithCustomFieldsSchema>;

export const GetIdeaViewByIdResponseSchema = inferApiResultSchema(IdeaViewWithCustomFieldsSchema);
export type GetIdeaViewByIdResponseDto = z.infer<typeof GetIdeaViewByIdResponseSchema>;

// GET /api/v1/idea-views
export const GetIdeaViewsResponseSchema = inferPaginatedApiResponse(
  z.array(IdeaViewListEntrySchema),
);
export type GetIdeaViewsResponseDto = z.infer<typeof GetIdeaViewsResponseSchema>;

// POST /api/v1/idea-views
export const CreateIdeaViewSchema = IdeaViewSchema.omit({
  id: true,
  accountId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  archivedAt: true,
  ownerId: true,
}).extend({
  prioritizationFrameworkId: z.number().optional(),
});
export type CreateIdeaViewDto = z.infer<typeof CreateIdeaViewSchema>;
export const CreateIdeaViewResponseSchema = inferApiResultSchema(IdeaViewSchema);
export type CreateIdeaViewResponseDto = z.infer<typeof CreateIdeaViewResponseSchema>;

// PUT /api/v1/idea-views/:id
export const UpdateIdeaViewSchema = IdeaViewSchema.omit({
  id: true,
  accountId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  archivedAt: true,
}).partial();
export type UpdateIdeaViewDto = z.infer<typeof UpdateIdeaViewSchema>;
export const UpdateIdeaViewResponseSchema = inferApiResultSchema(IdeaViewSchema);
export type UpdateIdeaViewResponseDto = z.infer<typeof UpdateIdeaViewResponseSchema>;
